import type { ReactNode } from "react";

interface MonthNavigatorProps {
  currentMonth: Date;
  onPrev: () => void;
  onNext: () => void;
  /** 월 선택 우측에 붙는 작은 컨트롤(예: 리스트 정산 필터) */
  endAdornment?: ReactNode;
}

/**
 * 오늘이 속한 달부터 앞으로 열 수 있는 달 수.
 * 다음 달과 그 이후 정산 예정까지 같은 방식으로 이동하고, 그 너머는 열지 않는다.
 */
export const MAX_FUTURE_MONTHS_AHEAD = 12;

function monthIndex(date: Date): number {
  return date.getFullYear() * 12 + date.getMonth();
}

/** 과거는 제한하지 않고, 미래는 오늘 기준 MAX_FUTURE_MONTHS_AHEAD 달까지 허용한다. */
export function isSettlementMonthOpen(month: Date, now = new Date()): boolean {
  return monthIndex(month) <= monthIndex(now) + MAX_FUTURE_MONTHS_AHEAD;
}

function formatMonthLabel(date: Date): string {
  const now = new Date();
  if (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth()
  ) {
    return "이번달";
  }
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
}

export default function MonthNavigator({
  currentMonth,
  onPrev,
  onNext,
  endAdornment,
}: MonthNavigatorProps) {
  const controls = (
    <div className="flex max-w-full items-center justify-center gap-3 sm:gap-6">
      <button
        type="button"
        onClick={onPrev}
        aria-label="이전 달"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-400 text-white"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <span className="min-w-[4.5rem] whitespace-nowrap text-center text-lg font-semibold text-gray-900">
        {formatMonthLabel(currentMonth)}
      </span>
      <button
        type="button"
        onClick={onNext}
        aria-label="다음 달"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );

  if (!endAdornment) {
    return (
      <div className="w-full max-w-full px-4 pt-4 pb-2">
        {controls}
      </div>
    );
  }

  // 360px 전후에서는 월 이동과 필터를 한 줄에 두면 오른쪽 화살표가 잘린다.
  return (
    <div className="w-full max-w-full px-4 pt-4 pb-2">
      <div className="flex w-full max-w-full flex-col items-center gap-3 sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center sm:gap-x-2 sm:gap-y-1">
        <div aria-hidden className="hidden min-w-0 sm:block" />
        <div className="relative z-10 flex min-w-0 justify-center">{controls}</div>
        <div className="flex w-full min-w-0 items-center justify-center sm:justify-end">{endAdornment}</div>
      </div>
    </div>
  );
}
